import React, { useState } from "react";
import { NavLink, Link, useLocation, Outlet } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X, ChevronRight, Sparkles, HelpCircle } from "lucide-react";
import { Logo } from "@/components/Logo";
import { ThemeToggle } from "@/components/ThemeToggle";
import { NotificationBell } from "@/components/shell/NotificationBell";
import { ProfileMenu } from "@/components/shell/ProfileMenu";
import { CommandPalette } from "@/components/shell/CommandPalette";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/context/AuthContext";

const initials = (u) => {
    if (!u) return "?";
    const src = u.name || u.email || "";
    return src.split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
};

const SidebarItem = ({ to, icon: Icon, label, badge, testId, comingSoon, exact }) => (
    <NavLink
        to={to}
        end={exact}
        data-testid={testId}
        className={({ isActive }) =>
            `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
                isActive
                    ? "bg-brand-navy text-white dark:bg-slate-800"
                    : "text-foreground/80 hover:bg-accent hover:text-foreground"
            }`
        }
    >
        <Icon size={17} className="shrink-0" />
        <span className="flex-1 truncate">{label}</span>
        {comingSoon && (
            <span className="text-[9px] font-bold uppercase tracking-wider rounded-full bg-amber-500/15 text-amber-600 dark:text-amber-400 px-2 py-0.5">
                Soon
            </span>
        )}
        {typeof badge === "number" && badge > 0 && (
            <span className="rounded-full bg-brand-green text-white text-[10px] font-bold px-1.5 min-w-[18px] h-[18px] flex items-center justify-center">
                {badge > 9 ? "9+" : badge}
            </span>
        )}
    </NavLink>
);

/**
 * DashboardLayout — shared shell used by Talent, Employer, Recruiter dashboards.
 *
 * Props:
 *   sidebar: array of { section, items: [{ to, icon, label, testId, comingSoon, badge, exact }] }
 *   footer: optional React node rendered inside the sidebar (e.g. "Upgrade" card)
 *   crumbs: array of { label, to } — top-bar breadcrumbs (last is not linked)
 *   title, subtitle: optional page header rendered above <Outlet />
 */
export const DashboardLayout = ({ sidebar = [], footer = null, crumbs = [], title, subtitle }) => {
    const { user } = useAuth();
    const location = useLocation();
    const [mobileOpen, setMobileOpen] = useState(false);

    // Auto-close mobile drawer on nav
    React.useEffect(() => { setMobileOpen(false); }, [location.pathname]);

    const SidebarContent = (
        <div className="flex flex-col h-full">
            <Link to="/" data-testid="dash-sidebar-logo" className="px-4 py-4 border-b border-border">
                <Logo size="lg" />
            </Link>
            <nav className="flex-1 overflow-y-auto p-3 space-y-6">
                {sidebar.map((sec) => (
                    <div key={sec.section}>
                        {sec.section && (
                            <div className="px-3 mb-2 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{sec.section}</div>
                        )}
                        <div className="space-y-0.5">
                            {sec.items.map((it) => <SidebarItem key={it.to} {...it} />)}
                        </div>
                    </div>
                ))}
                {footer && <div className="pt-4">{footer}</div>}
            </nav>
            <div className="p-3 border-t border-border flex items-center gap-3">
                <Avatar className="w-9 h-9">
                    <AvatarFallback className="bg-brand-green text-white text-xs font-bold">{initials(user)}</AvatarFallback>
                </Avatar>
                <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold text-foreground truncate">{user?.name || user?.email}</div>
                    <div className="text-[11px] text-muted-foreground capitalize truncate">{user?.role}</div>
                </div>
                <Link to="/contact" data-testid="dash-sidebar-support" className="text-muted-foreground hover:text-foreground" aria-label="Support">
                    <HelpCircle size={16} />
                </Link>
            </div>
        </div>
    );

    return (
        <div className="min-h-screen bg-background flex" data-testid="dashboard-layout">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col w-64 border-r border-border shrink-0 sticky top-0 h-screen bg-card/40">
                {SidebarContent}
            </aside>

            {/* Mobile drawer */}
            {mobileOpen && (
                <>
                    <div
                        className="fixed inset-0 bg-black/50 z-40 lg:hidden"
                        onClick={() => setMobileOpen(false)}
                    />
                    <motion.aside
                        initial={{ x: -280 }}
                        animate={{ x: 0 }}
                        exit={{ x: -280 }}
                        transition={{ duration: 0.2 }}
                        className="fixed top-0 left-0 bottom-0 w-72 bg-background border-r border-border z-50 lg:hidden"
                    >
                        {SidebarContent}
                    </motion.aside>
                </>
            )}

            <div className="flex-1 min-w-0 flex flex-col">
                {/* Top bar */}
                <header className="sticky top-0 z-30 backdrop-blur bg-background/80 border-b border-border">
                    <div className="flex items-center justify-between gap-4 px-4 sm:px-6 h-16">
                        <div className="flex items-center gap-2 min-w-0">
                            <button
                                className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-border"
                                onClick={() => setMobileOpen(true)}
                                aria-label="Open menu"
                                data-testid="dash-mobile-menu"
                            >
                                <Menu size={16} />
                            </button>
                            <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground min-w-0 hidden sm:flex items-center gap-1.5 truncate">
                                {crumbs.map((c, i) => (
                                    <span key={i} className="inline-flex items-center gap-1.5 truncate">
                                        {i > 0 && <ChevronRight size={12} className="opacity-50" />}
                                        {c.to && i < crumbs.length - 1 ? (
                                            <Link to={c.to} className="hover:text-foreground truncate">{c.label}</Link>
                                        ) : (
                                            <span className="text-foreground font-medium truncate">{c.label}</span>
                                        )}
                                    </span>
                                ))}
                            </nav>
                        </div>
                        <div className="flex items-center gap-2">
                            <CommandPalette />
                            <ThemeToggle />
                            <NotificationBell />
                            <ProfileMenu />
                        </div>
                    </div>
                </header>

                {/* Page header + content */}
                <main className="flex-1 p-4 sm:p-6 lg:p-8">
                    {(title || subtitle) && (
                        <div className="mb-6">
                            {title && <h1 data-testid="dash-page-title" className="font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">{title}</h1>}
                            {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
                        </div>
                    )}
                    <Outlet />
                </main>
            </div>
        </div>
    );
};

/* Small stat card used across dashboards */
export const StatCard = ({ icon: Icon, label, value, sub, delta, accent = "green", testId, to }) => {
    const accents = {
        green: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400",
        blue: "bg-blue-500/15 text-blue-600 dark:text-blue-400",
        violet: "bg-violet-500/15 text-violet-600 dark:text-violet-400",
        amber: "bg-amber-500/15 text-amber-600 dark:text-amber-400",
        pink: "bg-pink-500/15 text-pink-600 dark:text-pink-400",
        slate: "bg-muted text-muted-foreground",
    };
    const Content = (
        <div className="card-soft p-5 flex flex-col group hover:border-emerald-500/40 transition-colors">
            <div className="flex items-center justify-between">
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accents[accent] || accents.green}`}>
                    <Icon size={16} />
                </div>
                {delta && (
                    <span className="text-[10px] font-bold text-emerald-500">{delta}</span>
                )}
            </div>
            <div className="mt-4 mono-score text-2xl sm:text-3xl font-bold text-foreground">{value}</div>
            <div className="mt-0.5 text-sm font-medium text-foreground">{label}</div>
            {sub && <div className="mt-0.5 text-xs text-muted-foreground line-clamp-2">{sub}</div>}
        </div>
    );
    return to ? (
        <Link to={to} data-testid={testId} className="block">{Content}</Link>
    ) : (
        <div data-testid={testId}>{Content}</div>
    );
};

/* Coming Soon placeholder used across dashboards */
export const ComingSoon = ({ title, description, icon: Icon = Sparkles, waitlist, previewImg, testId = "coming-soon" }) => (
    <div data-testid={testId} className="max-w-3xl">
        <div className="card-soft p-10 relative overflow-hidden">
            <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-emerald-500/10 blur-3xl pointer-events-none" />
            <div className="relative">
                <div className="inline-flex items-center gap-2 rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 px-3 py-1 text-[10px] font-bold uppercase tracking-wider">
                    <Icon size={12} /> Coming Soon
                </div>
                <h1 className="mt-4 font-heading font-medium text-3xl sm:text-4xl tracking-tight text-foreground">{title}</h1>
                <p className="mt-3 text-muted-foreground max-w-xl leading-relaxed">{description}</p>
                {previewImg && <img src={previewImg} alt="preview" className="mt-6 rounded-2xl border border-border max-w-full" />}
                {waitlist && (
                    <div className="mt-6 flex items-center gap-2">
                        <a href="mailto:hello@simonaratalent.us?subject=Join%20waitlist" className="btn-green text-sm">Join waitlist</a>
                        <a href="/contact" className="btn-outline text-sm">Get in touch</a>
                    </div>
                )}
            </div>
        </div>
    </div>
);

export default DashboardLayout;
